import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import { unwrap } from '../lib/api-envelope';
import { LOAD_FAILURE_QUERY_OPTIONS } from '../lib/load-failure';
import type { MaintenanceFlagsResponse } from '../types/maintenance-flags';

/**
 * Maintenance watch flags on one asset or one job (api#944).
 *
 * The roles mirror the API's FLAG_READ_ROLES / FLAG_ANSWER_ROLES exactly.
 * `owner` is in neither: the API's requireRole admits only the roles it lists,
 * so an owner would draw a 403 on every visit. A role outside the read set
 * makes no request at all.
 *
 * A dispatcher READS and does not answer. An admin-elevated dispatcher is
 * admitted to answer by the API, but the elevation is a per-tenant server-side
 * lookup this client does not have, so the buttons stay hidden for every
 * dispatcher — the conservative side of the gap.
 */
const READ_ROLES = new Set(['admin', 'office_staff', 'supervisor', 'dispatcher']);
const ANSWER_ROLES = new Set(['admin', 'office_staff', 'supervisor']);

export const canReadMaintenanceFlags = (role: string | null | undefined) => !!role && READ_ROLES.has(role);
export const canAnswerMaintenanceFlags = (role: string | null | undefined) => !!role && ANSWER_ROLES.has(role);

export type MaintenanceFlagsSubject = { kind: 'equipment'; id: string } | { kind: 'job'; id: string };

/** Every maintenance-flag query starts with this, so one invalidation reaches both mounts. */
export const MAINTENANCE_FLAGS_ROOT = ['maintenance-flags'] as const;

export const maintenanceFlagsKey = (subject: MaintenanceFlagsSubject) =>
  ['maintenance-flags', subject.kind, subject.id] as const;

export function useMaintenanceFlags(subject: MaintenanceFlagsSubject, role: string | null | undefined) {
  return useQuery({
    queryKey: maintenanceFlagsKey(subject),
    queryFn: async () => {
      const res =
        subject.kind === 'equipment'
          ? await api.maintenanceFlags.forEquipment(subject.id)
          : await api.maintenanceFlags.forJob(subject.id);
      return unwrap<MaintenanceFlagsResponse>(res.data);
    },
    enabled: !!subject.id && canReadMaintenanceFlags(role),
    // Retries a dead server, never a refusal, and keeps the failure where the
    // panel can see it rather than parking the query in `paused`.
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}
